import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wand2, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReframingTool = ({ argumentId, originalText, onReframeAccepted }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const generateSuggestions = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/argument/${argumentId}/reframe`);
      setSuggestions(response.data.suggestions || []);
      setShowSuggestions(true);
      toast.success('Reframing suggestions generated!');
    } catch (error) {
      console.error('Error generating suggestions:', error);
      toast.error('Failed to generate suggestions');
    } finally {
      setLoading(false);
    }
  };

  const acceptSuggestion = (reframedText) => {
    if (onReframeAccepted) {
      onReframeAccepted(reframedText);
    }
    toast.success('Suggestion applied!');
    setShowSuggestions(false);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'clarity': return 'bg-blue-100 text-blue-800';
      case 'empathy': return 'bg-green-100 text-green-800';
      case 'logic': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="reframing-tool">
      {!showSuggestions ? (
        <Button
          variant="outline"
          size="sm"
          onClick={generateSuggestions}
          disabled={loading}
          className="w-full"
          data-testid="generate-reframe-btn"
        >
          <Wand2 className="h-4 w-4 mr-2" />
          {loading ? 'Generating...' : 'Get Reframing Suggestions'}
        </Button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Reframing Suggestions</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSuggestions(false)}
            >
              Close
            </Button>
          </div>

          {suggestions.map((suggestion, idx) => (
            <Card key={idx} className="border-l-4">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Badge className={getTypeColor(suggestion.improvement_type)}>
                    {suggestion.improvement_type.toUpperCase()}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => acceptSuggestion(suggestion.reframed)}
                    data-testid={`accept-reframe-${idx}-btn`}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Use This
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Reframed:</p>
                  <p className="text-sm">{suggestion.reframed}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    <strong>Why:</strong> {suggestion.explanation}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}

          <Button
            variant="outline"
            size="sm"
            onClick={generateSuggestions}
            disabled={loading}
            className="w-full"
          >
            Generate New Suggestions
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReframingTool;